import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "@/app/layout/AppLayout";
import { AuthLayout } from "@/app/layout/AuthLayout";
import { Skeleton } from "@/components/ui/skeleton";

const LoginPage = lazy(() =>
  import("@/features/auth/pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("@/features/auth/pages/RegisterPage").then((m) => ({ default: m.RegisterPage })),
);
const PendingApprovalPage = lazy(() =>
  import("@/features/auth/pages/PendingApprovalPage").then((m) => ({
    default: m.PendingApprovalPage,
  })),
);
const ActivateAccountPage = lazy(() =>
  import("@/features/auth/pages/ActivateAccountPage").then((m) => ({
    default: m.ActivateAccountPage,
  })),
);
const OnboardingPage = lazy(() =>
  import("@/features/auth/pages/OnboardingPage").then((m) => ({ default: m.OnboardingPage })),
);
const OAuthCallbackPage = lazy(() =>
  import("@/features/auth/pages/OAuthCallbackPage").then((m) => ({ default: m.OAuthCallbackPage })),
);
const DashboardPage = lazy(() =>
  import("@/features/dashboard/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const LaunchesPage = lazy(() =>
  import("@/features/launches/pages/LaunchesPage").then((m) => ({ default: m.LaunchesPage })),
);
const LaunchFormPage = lazy(() =>
  import("@/features/launches/pages/LaunchFormPage").then((m) => ({ default: m.LaunchFormPage })),
);
const UnitsPage = lazy(() =>
  import("@/features/units/pages/UnitsPage").then((m) => ({ default: m.UnitsPage })),
);
const UnitDetailPage = lazy(() =>
  import("@/features/units/pages/UnitDetailPage").then((m) => ({ default: m.UnitDetailPage })),
);
const SalesNetworkPage = lazy(() =>
  import("@/features/network/pages/SalesNetworkPage").then((m) => ({ default: m.SalesNetworkPage })),
);
const BrokersPage = lazy(() =>
  import("@/features/brokers/pages/BrokersPage").then((m) => ({ default: m.BrokersPage })),
);
const ProposalsPage = lazy(() =>
  import("@/features/proposals/pages/ProposalsPage").then((m) => ({ default: m.ProposalsPage })),
);
const ProposalDetailPage = lazy(() =>
  import("@/features/proposals/pages/ProposalDetailPage").then((m) => ({
    default: m.ProposalDetailPage,
  })),
);
const MediaPage = lazy(() =>
  import("@/features/media/pages/MediaPage").then((m) => ({ default: m.MediaPage })),
);
const BroadcastDetailPage = lazy(() =>
  import("@/features/media/pages/BroadcastDetailPage").then((m) => ({
    default: m.BroadcastDetailPage,
  })),
);
const UsersPage = lazy(() =>
  import("@/features/users/pages/UsersPage").then((m) => ({ default: m.UsersPage })),
);
const SettingsPage = lazy(() =>
  import("@/features/settings/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const NotificationsPage = lazy(() =>
  import("@/features/notifications/pages/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const TicketsPage = lazy(() =>
  import("@/features/tickets/pages/TicketsPage").then((m) => ({ default: m.TicketsPage })),
);
const NewTicketPage = lazy(() =>
  import("@/features/tickets/pages/NewTicketPage").then((m) => ({ default: m.NewTicketPage })),
);
const TicketDetailPage = lazy(() =>
  import("@/features/tickets/pages/TicketDetailPage").then((m) => ({
    default: m.TicketDetailPage,
  })),
);

function PageFallback() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <Skeleton className="h-96" />
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [
      { path: "/login", element: <Lazy><LoginPage /></Lazy> },
      { path: "/register", element: <Lazy><RegisterPage /></Lazy> },
      { path: "/onboarding", element: <Lazy><OnboardingPage /></Lazy> },
      { path: "/pending", element: <Lazy><PendingApprovalPage /></Lazy> },
      { path: "/activate-account", element: <Lazy><ActivateAccountPage /></Lazy> },
    ],
  },
  // Fora do AuthLayout: o callback troca o code e roteia sozinho (sem o redirect
  // de "já autenticado" do layout e sem o enquadramento de card).
  { path: "/auth/callback/:provider", element: <Lazy><OAuthCallbackPage /></Lazy> },
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "/dashboard", element: <Lazy><DashboardPage /></Lazy> },
      { path: "/launches", element: <Lazy><LaunchesPage /></Lazy> },
      { path: "/launches/new", element: <Lazy><LaunchFormPage /></Lazy> },
      { path: "/launches/:id/edit", element: <Lazy><LaunchFormPage /></Lazy> },
      { path: "/units", element: <Lazy><UnitsPage /></Lazy> },
      { path: "/units/:id", element: <Lazy><UnitDetailPage /></Lazy> },
      { path: "/brokers", element: <Lazy><BrokersPage /></Lazy> },
      { path: "/network", element: <Lazy><SalesNetworkPage /></Lazy> },
      { path: "/proposals", element: <Lazy><ProposalsPage /></Lazy> },
      { path: "/proposals/:id", element: <Lazy><ProposalDetailPage /></Lazy> },
      { path: "/media", element: <Lazy><MediaPage /></Lazy> },
      { path: "/media/:id", element: <Lazy><BroadcastDetailPage /></Lazy> },
      { path: "/users", element: <Lazy><UsersPage /></Lazy> },
      { path: "/settings", element: <Lazy><SettingsPage /></Lazy> },
      { path: "/notifications", element: <Lazy><NotificationsPage /></Lazy> },
      { path: "/support", element: <Lazy><TicketsPage /></Lazy> },
      { path: "/support/new", element: <Lazy><NewTicketPage /></Lazy> },
      { path: "/support/:id", element: <Lazy><TicketDetailPage /></Lazy> },
    ],
  },
  { path: "*", element: <Navigate to="/dashboard" replace /> },
]);
